import { getToken } from 'actions';

const wsUrl = process.env.REACT_APP_API_MONITORA.replace('https', 'wss').replace('http', 'ws');
const ROOM_JOIN_TIMEOUT_MS = 15000;
const DELAY_BETWEEN_COMMANDS_MS = 300;

const buildJoinPayload = (serial, user) => ({
  type: 'join',
  room: serial,
  user_id: user?._id,
  name: user?.name,
  email: user?.email,
  client: 'client',
  page: 'support',
  token: getToken(),
  content: {
    name: `${user?.email || 'user'}-API-SUPORTE-BULK`,
  },
});

const buildProcessPayload = (serial, command) => ({
  type: 'message',
  room: serial,
  content: {
    command: 'process',
    parameters: {
      command: command.trim(),
    },
  },
});

/**
 * Envia o mesmo comando shell (process) para vários equipamentos via WebSocket.
 * Usa o mesmo protocolo da tela Conected, sem abrir stream de vídeo.
 */
export const sendBulkProcessCommands = ({ serials, command, user, onProgress, signal }) => {
  const trimmedCommand = (command || '').trim();

  if (!trimmedCommand) {
    return Promise.reject(new Error('Informe o comando a ser enviado.'));
  }

  if (!serials?.length) {
    return Promise.reject(new Error('Selecione ao menos um equipamento.'));
  }

  return new Promise((resolve, reject) => {
    const socket = new WebSocket(wsUrl);
    let pingInterval = null;
    let sessionReady = false;
    let pendingJoin = null;
    let bulkStarted = false;
    let finished = false;
    const results = [];

    const cleanup = () => {
      if (pingInterval) {
        clearInterval(pingInterval);
        pingInterval = null;
      }
      if (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING) {
        socket.close();
      }
    };

    const failPendingJoin = (error) => {
      if (!pendingJoin) return;
      const { reject: rejectJoin, timeoutId } = pendingJoin;
      clearTimeout(timeoutId);
      pendingJoin = null;
      rejectJoin(error);
    };

    const resolvePendingJoin = () => {
      if (!pendingJoin) return;
      const { resolve: resolveJoin, timeoutId } = pendingJoin;
      clearTimeout(timeoutId);
      pendingJoin = null;
      resolveJoin();
    };

    const joinRoom = (serial) =>
      new Promise((resolveJoin, rejectJoin) => {
        const timeoutId = setTimeout(() => {
          if (pendingJoin) {
            pendingJoin = null;
          }
          rejectJoin(new Error('Tempo esgotado ao conectar ao equipamento'));
        }, ROOM_JOIN_TIMEOUT_MS);

        pendingJoin = { resolve: resolveJoin, reject: rejectJoin, timeoutId, serial };
        socket.send(JSON.stringify(buildJoinPayload(serial, user)));
      });

    const sendCommandToRoom = (serial) => {
      socket.send(JSON.stringify(buildProcessPayload(serial, trimmedCommand)));
    };

    const runBulk = async () => {
      if (bulkStarted) return;
      bulkStarted = true;

      for (const serial of serials) {
        if (signal?.aborted) {
          results.push({ serial, status: 'cancelled', message: 'Operação cancelada' });
          onProgress?.({ serial, status: 'cancelled' });
          break;
        }

        onProgress?.({ serial, status: 'connecting' });

        try {
          await joinRoom(serial);
          sendCommandToRoom(serial);
          results.push({ serial, status: 'success' });
          onProgress?.({ serial, status: 'success' });
        } catch (err) {
          const message = err?.message || 'Falha ao enviar comando';
          results.push({ serial, status: 'error', message });
          onProgress?.({ serial, status: 'error', message });
        }

        if (signal?.aborted) break;

        await new Promise((r) => setTimeout(r, DELAY_BETWEEN_COMMANDS_MS));
      }

      finished = true;
      cleanup();
      resolve(results);
    };

    socket.onopen = () => {
      pingInterval = setInterval(() => {
        if (socket.readyState === WebSocket.OPEN) {
          socket.send(JSON.stringify({ type: 'ping' }));
        }
      }, 20000);
    };

    socket.onmessage = (event) => {
      let msg;
      try {
        msg = JSON.parse(event.data);
      } catch {
        return;
      }

      if (msg.type === 'invalid_user') {
        const error = new Error(msg?.msg || 'Sem permissão para executar esta ação');
        if (!sessionReady) {
          cleanup();
          reject(error);
          return;
        }
        if (pendingJoin) {
          failPendingJoin(error);
        }
        return;
      }

      if (msg.pubkey && !sessionReady) {
        sessionReady = true;
        runBulk();
        return;
      }

      if (pendingJoin && (msg.type === 'valid_user' || msg.type === 'connected')) {
        resolvePendingJoin();
      }
    };

    socket.onerror = () => {
      cleanup();
      reject(new Error('Erro na conexão WebSocket'));
    };

    socket.onclose = (event) => {
      if (finished || signal?.aborted) return;
      if (!bulkStarted) {
        reject(new Error('Conexão WebSocket encerrada antes de iniciar o envio'));
        return;
      }
      if (event.code !== 1000) {
        reject(new Error('Conexão WebSocket encerrada antes de concluir o envio'));
      }
    };

    if (signal) {
      signal.addEventListener('abort', () => {
        cleanup();
      });
    }
  });
};
